import { gql, useMutation } from "@apollo/client";
import {
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { DateField } from '@mui/x-date-pickers/DateField';
import pick from "lodash/fp/pick";
import React, { useState } from "react";

import { PhoneRegex } from "@caredna-marlin/utils/addresses";
import { dbDate, parseDbDate } from "@caredna-marlin/utils/dates";

import AddressForm from "~/components/common/forms/AddressForm";
import KeyValueList from "~/components/common/forms/KeyValueList";
import PatientSexForm from "~/components/common/forms/PatientSexForm";
import { addressKeys } from "@caredna-marlin/utils/client/geocoding";
import { PatientSexEnum } from "~/graphql/__generated__/graphql";
import { Patient } from "~/utils/forms";

type Props = {
  onClose: (newPatient?: Patient) => void;
  patient?: Patient;
}

const newPatient: Patient = {
  id: 0,
  firstName: "",
  lastName: "",
  dateOfBirth: null,
  phone: null,
  sex: PatientSexEnum.Unknown,
  patientNotes: "",
  serviceInstructions: "",
  addressStreet: null,
  addressCity: null,
  addressState: null,
  addressZip: null,
  addressTimezone: null,
  patientIdentifiers: [],
  episodes: [],
}

const EditPatient: React.FC<Props> = ({ onClose, patient = newPatient}) => {
  const [edited, setEdited] = useState(patient);
  const [createPatient, createStatus] = useMutation(CREATE_PATIENT);
  const [updatePatient, updateStatus] = useMutation(UPDATE_PATIENT);
  const loading = createStatus.loading || updateStatus.loading;

  const phoneError = !!edited.phone && !edited.phone.match(PhoneRegex);
  const dobError = !!edited.dateOfBirth && new Date(edited.dateOfBirth) > new Date();
  // @ts-expect-error The types are messed up but this is where the data is
  const addressError = (createStatus.error || updateStatus.error)?.networkError?.result?.errors?.[0]?.message.includes("Address")

  const onSave = async () => {
    const patientIdentifiers = (edited.patientIdentifiers || [])
      .filter(pid => pid.identifier && pid.identifier_type)
      .map(pick(["identifier", "identifier_type"]));

    const variables = {
      ...edited,
      patientId: edited.id,
      patientIdentifiers,
    };

    try {
      let result;
      if (variables.patientId) {
        result = await updatePatient({ variables, awaitRefetchQueries: true, refetchQueries: "active" });
      } else {
        result = await createPatient({ variables, awaitRefetchQueries: true, refetchQueries: "active" });
      }

      if (!result.errors) {
        onClose(variables.patientId ? undefined : result.data.createPatient);
      }
    } catch (e) {
      // Do nothing; display is already handled elsewhere.
      console.error(e);
    }
  }

  const closeWithoutResult = () => onClose();

  const addressNotPresent =  !edited.addressStreet || !edited.addressCity || !edited.addressState || !edited.addressZip;

  const shouldDisableSave = loading || phoneError || dobError || !edited.firstName || !edited.lastName || addressNotPresent;

  return (<Dialog open={true} onClose={closeWithoutResult} fullWidth maxWidth="lg">
    <DialogTitle>
      {patient.id ? `Editing ${patient.firstName} ${patient.lastName}` : "Add Patient"}
    </DialogTitle>
    <DialogContent>
      <Grid container spacing={2}>
        <Grid size={{xs: 12, lg: 6}}>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={6}>
              <TextField
                required
                label="First"
                fullWidth
                autoCorrect="off"
                defaultValue={edited.firstName || ""}
                onChange={(e) => setEdited({...edited, firstName: e.target.value })}
                disabled={loading}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                required
                label="Last"
                fullWidth
                autoCorrect="off"
                value={edited.lastName || ""}
                onChange={(e) => setEdited({ ...edited, lastName: e.target.value })}
                disabled={loading}
              />
            </Grid>

            <Grid size={4}>
              <DateField
                label="Date of Birth"
                fullWidth
                clearable
                maxDate={new Date()}
                value={edited.dateOfBirth ? parseDbDate(edited.dateOfBirth) : undefined}
                onChange={(date) => {
                  const dateOfBirth = date && !isNaN(date.getTime()) ? dbDate(date) : null;
                  setEdited({ ...edited, dateOfBirth })
                }}
                disabled={loading}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                label="Phone number"
                value={edited.phone || ""}
                onChange={(e) => setEdited({ ...edited, phone: e.target.value })}
                error={phoneError}
                helperText={phoneError ? "Invalid phone number" : undefined}
                disabled={loading}
              />
            </Grid>
            <Grid size={4}>
              <PatientSexForm
                label="Sex"
                fullWidth
                sex={edited.sex || null}
                setSex={(sex) => setEdited({ ...edited, sex })}
                disabled={loading}
              />
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                control={<Checkbox defaultChecked={true} />}
                label="Patient consents to SMS communication"
              />
            </Grid>

            <Grid size={12}>
              <Divider />
              <AddressForm
                required
                address={pick(addressKeys, edited)}
                setAddress={(address) => setEdited({ ...edited, ...address })}
                disabled={loading}
                error={addressError}
              />
            </Grid>

            <Grid size={12} sx={{mt:1}}>
              <Typography variant="caption">Patient Identifiers</Typography>
              <KeyValueList
                keyProp="identifier_type"
                keyPlaceholder="MRN"
                valueProp="identifier"
                valuePlaceholder="CC-1234567"
                value={edited.patientIdentifiers}
                onChange={(newIdent) => setEdited({...edited, patientIdentifiers: newIdent as any})}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{xs: 12, lg: 6}}>
          <TextField
            label="Patient Notes"
            multiline
            fullWidth
            rows={5}
            sx={{ mt: 1 }}
            placeholder={`Example: "Pt is BID, flex and needs meals"`}
            value={edited.patientNotes || ""}
            onChange={(e) => setEdited({...edited, patientNotes: e.target.value})}
            disabled={loading}
            helperText={`Use this as your notepad for things to remember about the patient or constraints on how to schedule their visits.`}
          />

          <TextField
            label="Service Instructions"
            multiline
            fullWidth
            rows={5}
            sx={{ mt: 1 }}
            placeholder={`Example: "Door access code is 1234, patient has scary dog, no one home to answer door - door is unlocked"`}
            value={edited.serviceInstructions || ""}
            onChange={(e) => setEdited({...edited, serviceInstructions: e.target.value})}
            disabled={loading}
            helperText={`Use this for specific notes and/or instructions regarding accessing the patient's home. This will update the service instructions for any future visits.`}
          />
        </Grid>
      </Grid>
    </DialogContent>
    <DialogActions>
      <Button disabled={loading} onClick={closeWithoutResult}>
        Cancel
      </Button>
      <Button loading={loading} disabled={shouldDisableSave} onClick={onSave} color="primary">
        Save
      </Button>
    </DialogActions>
  </Dialog>);
}

export default EditPatient;

export const CREATE_PATIENT = gql(`
  mutation CreatePatient(
    $firstName: String!
    $lastName: String!
    $phone: String
    $addressStreet: String!
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $sex: PatientSexEnum
    $dateOfBirth: String
    $patientNotes: String
    $serviceInstructions: String
    $patientIdentifiers: [ManagePatientIdentifier!]!
    $addressPlaceId: String
  )
  @permission(requires: managePatients)
  {
    createPatient(
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      addressStreet: $addressStreet
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      dateOfBirth: $dateOfBirth
      sex: $sex
      patientNotes: $patientNotes
      serviceInstructions: $serviceInstructions
      patientIdentifiers: $patientIdentifiers
      addressPlaceId: $addressPlaceId
    ) {
      id
      firstName
      lastName
      addressTimezone
    }
  }
`);

const UPDATE_PATIENT = gql(`
  mutation UpdatePatient(
    $patientId: Int!
    $firstName: String!
    $lastName: String!
    $phone: String
    $addressStreet: String!
    $addressLineTwo: String
    $addressCity: String
    $addressState: String
    $addressZip: String
    $dateOfBirth: String
    $sex: PatientSexEnum
    $patientNotes: String
    $serviceInstructions: String
    $patientIdentifiers: [ManagePatientIdentifier!]!
    $addressPlaceId: String
  )
  @permission(requires: managePatients)
  {
    updatePatient(
      id: $patientId
      firstName: $firstName
      lastName: $lastName
      phone: $phone
      addressStreet: $addressStreet
      addressLineTwo: $addressLineTwo
      addressCity: $addressCity
      addressState: $addressState
      addressZip: $addressZip
      dateOfBirth: $dateOfBirth
      sex: $sex
      patientNotes: $patientNotes
      serviceInstructions: $serviceInstructions
      patientIdentifiers: $patientIdentifiers
      addressPlaceId: $addressPlaceId
    ) {
      id
      firstName
      lastName
      phone
      addressStreet
      addressLineTwo
      addressCity
      addressState
      addressZip
      dateOfBirth
      sex
      patientNotes
      serviceInstructions
      patientIdentifiers {
        id
        identifier
        identifier_type
      }
      updatedAt
    }
  }
`);
